/**
 * <StudySkyDelight /> — Cycle 18 · one-time first-night delight.
 * ─────────────────────────────────────────────────────────────
 * On the very first time a child toggles Night mode, we play a
 * ~3 s constellation-draw of a fennec-fox silhouette (Foxy) over a
 * starry night sky, tagged "Even at night, we're exploring…".
 * After the first show it never returns — persisted in
 * localStorage. Respects `prefers-reduced-motion` (fades instead
 * of drawing).
 *
 * Triggered by the `mwq-night-toggled-on` custom event dispatched
 * by <NightModeToggle /> when the user flips to night.
 */
import React, { useEffect, useState } from "react";
import { InkThroughReveal } from "@/components/paperAtlas/InkThroughReveal";

const KEY = "mwq_study_sky_seen_v1";

// Fennec fox constellation — 12 line-segments over a 400×400 canvas.
// Traces two big ears, a rounded head, a small body, a bushy tail.
const FOXY_PATH = "M 118 92 L 152 176 L 186 128 L 220 76 L 258 168 L 296 130 L 292 210 L 260 260 L 296 302 L 254 314 L 208 302 L 168 316 L 130 268 L 92 236 L 108 168 L 152 176";

export default function StudySkyDelight() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onNight = () => {
      try {
        if (localStorage.getItem(KEY) === "1") return;   // already seen
      } catch { /* no storage — still show */ }
      setVisible(true);
    };
    window.addEventListener("mwq-night-toggled-on", onNight);
    return () => window.removeEventListener("mwq-night-toggled-on", onNight);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => close(), 4200);   // auto-dismiss
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const close = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* quota */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Welcome to Night mode"
      onClick={close}
      data-testid="study-sky-delight"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-night-950/95 backdrop-blur-sm cursor-pointer animate-page-turn"
    >
      {/* Starfield — 40 twinkling stars behind Foxy */}
      <svg aria-hidden className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
        {Array.from({ length: 60 }).map((_, i) => {
          const x = (i * 91) % 400;
          const y = ((i * 137) % 400);
          const r = ((i % 5) + 1) * 0.4 + 0.6;
          return <circle key={i} cx={x} cy={y} r={r} fill="#F5F7FF" opacity={(i % 4) * 0.15 + 0.35} />;
        })}
      </svg>
      <div className="relative flex flex-col items-center gap-6 px-6">
        <p className="text-starlight-400 font-heading text-sm uppercase tracking-[0.35em]">Welcome to Night</p>
        <div className="w-64 h-64 md:w-80 md:h-80">
          <InkThroughReveal
            d={FOXY_PATH}
            width={400}
            height={400}
            strokeColor="#F5C77A"
            fillColor="#4E5399"
            strokeWidth={2.5}
            autoPlay
          />
        </div>
        <p className="text-starlight-50 font-heading text-2xl md:text-3xl font-bold text-center max-w-md">
          Even at night,<br className="md:hidden"/> we&apos;re exploring.
        </p>
        <p className="text-starlight-400 text-xs">Tap anywhere to continue</p>
      </div>
    </div>
  );
}
